import { Card, CardContent, Skeleton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ReactElement, useContext, useEffect, useState } from "react";
import { MasterDataContext } from "contexts/MasterDataContext";
import BooksStatistic from "./BooksStatistic";
import GroupsIcon from "@mui/icons-material/Groups";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import BookIcon from "components/SVGs/Book";
import { useFetchGRStatisticsQuery } from "app/services/reports";
import { GrUsageChart } from "./BooksStatistic/ComposedChart";
import DataNotFound from "components/DataNotFound";

const customStyles = {
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    padding: "20px 0",
    width: "100%",
    "@media print": {
      padding: 0,
      colorAdjust: "exact",
      pageBreakAfter: "always",
      width: "100%",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    width: "100%",
    "& > span": {
      fontWeight: "bold",
    },
  },
  statistics: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "& > span": {
      padding: "10px 30px",
      fontWeight: "bold",
      fontSize: "14px",
    },
  },
  cards: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    width: "100%",
    padding: "20px 0 0",
    "@media print": {
      height: "70mm",
      width: "100%",
    },
  },
  card: {
    width: "275px",
    backgroundColor: "rgb(248, 248, 248)",
    color: "rgb(59, 59, 59)",
    height: "180px",
    margin: "0 15px",
    border: "none",
    borderRadius: "15px",
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
};
const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
interface IProps {}
const GuidedReading = (props: IProps) => {
  const {
    state: { schoolDetails },
  } = useContext(MasterDataContext);
  const { t, i18n } = useTranslation();
  const [academicYear, setAcademicYear] = useState<{
    start: string;
    end: string;
  }>();
  const [statistics, setStatistics] = useState<{
    activeStudents: number;
    spentTime: number;
    avgBooks: number;
  }>();
  const [chartData, setChartData] = useState<GrUsageChart[]>();
  const { data: grData, isLoading } = useFetchGRStatisticsQuery();

  useEffect(() => {
    if (grData) {
      const {
        _activeStudentPercentage,
        // _averageCompletedBooks,
        _averageTimeSpent,
        _monthlyBookReads,
        // _top8Achievers,
      } = grData._entity;
      setStatistics({
        activeStudents: _activeStudentPercentage,
        spentTime: _averageTimeSpent,
        avgBooks: _monthlyBookReads
          .map((item) => item._numberOfBooks)
          .reduce((item, acc) => acc + item, 0),
      });
      let chartRec: { [x: string]: string | number; xAxisLabel: string }[] = [];
      _monthlyBookReads.forEach((item) => {
        chartRec.push({
          xAxisLabel: t(item._monthName),
          [t("No Of Student")]: item._numberOfStudents,
          [t("Books Read")]: item._numberOfBooks,
        });
      });
      setChartData(chartRec);
    }
  }, [grData, i18n.dir()]);

  useEffect(() => {
    if (schoolDetails?.academicEndDate && schoolDetails?.academicStartDate) {
      const startDate = new Date(schoolDetails?.academicStartDate);
      const endDate = new Date(schoolDetails?.academicEndDate);
      setAcademicYear({
        start: `${t(months[startDate.getMonth()])}.${startDate.getFullYear()}`,
        end: `${t(months[new Date().getMonth()])}.${endDate.getFullYear()}`,
      });
    }
  }, [schoolDetails, i18n.dir()]);

  return (
    <Typography component="div" sx={customStyles.root}>
      {isLoading ? (
        <>
          <Skeleton variant="rectangular" height={250} width="90%" />
          <Skeleton
            variant="rectangular"
            height={350}
            width="90%"
            sx={{ margin: "10px 0" }}
          />
          <Skeleton variant="rectangular" height={350} width="90%" />
        </>
      ) : grData ? (
        <>
          <Typography component="div" sx={customStyles.header}>
            <Typography
              component="span"
              sx={{ fontSize: "22px", color: "rgb(60, 180, 229)" }}
            >
              {t("Guided Reading Usage Breakdown")}
            </Typography>
            <Typography component="span" sx={{ fontSize: "16px" }}>
              {academicYear?.start + " - " + academicYear?.end}
            </Typography>
            <Typography component="div" sx={customStyles.cards}>
              {generalStatistics(
                statistics?.activeStudents ?? 0,
                statistics?.spentTime ?? 0,
                statistics?.avgBooks ?? 0
              ).map((item) => (
                <StatisticCard key={item.title} {...item} />
              ))}
            </Typography>
          </Typography>
          <BooksStatistic chartData={chartData!} />
        </>
      ) : (
        <DataNotFound text={t("No data")} />
      )}
    </Typography>
  );
};
const generalStatistics = (
  activeStudents: number,
  spentTime: number,
  avgBooks: number
) => [
  {
    title: "% of active students",
    icon: () => (
      <GroupsIcon sx={{ fontSize: "90px", color: "rgb(174, 24, 67)" }} />
    ),
    content: activeStudents + "%",
  },
  {
    title: "Avg spent time per student",
    icon: () => (
      <WatchLaterIcon sx={{ fontSize: "80px", color: "rgb(128, 76, 240)" }} />
    ),
    content: spentTime + " min",
  },
  {
    title: "Avg completed books per student",
    content: avgBooks + " Books",
    icon: () => (
      <BookIcon sx={{ fontSize: "70px", color: "rgb(0, 117, 117)" }} />
    ),
  },
];
interface IStatisticCardProps {
  title: string;
  icon: () => ReactElement;
  content: string;
}
const StatisticCard = ({ title, icon, content }: IStatisticCardProps) => {
  const { t } = useTranslation();
  return (
    <Card sx={customStyles.card} variant="outlined">
      <CardContent sx={customStyles.cardContent}>
        <Typography
          sx={{
            fontSize: "20px",
            textAlign: "center",
          }}
        >
          {t(title)}
        </Typography>
        <Typography
          component="div"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "end",
          }}
        >
          {icon()}
          <Typography sx={{ fontSize: "18px" }} color="text.secondary">
            {content}
          </Typography>
        </Typography>
      </CardContent>
    </Card>
  );
};

export default GuidedReading;
